import { useState, useCallback, useEffect, useMemo } from 'react';
import {useSearchParams } from 'react-router-dom';
import { Box,  CircularProgress , Button, Typography } from '@mui/material';
import './Command.scss';
import CommandView from './CommandView';
import useThemeVal from '../../utility/useThemeVal';
import {cardClasses, commandHomeEndpoint} from '../utility/constants';
import apiService from '../../api/apiService';
import { hasPermissions, useToken } from '../../utility';
import { failsTokenAndSetupCheck } from '../../checks/setup';
import CropRunTab from './Tabs/CropRunTab';
import NoPermissionMsg from '../utility/NoPermissionMsg';


export default function Command() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]); // Fetched data state
  const [statusChoices, setStatusChoices] = useState([]); // Store API response
  const [tableData, setTableData] = useState([]);
  // const[phaseDetails,setPhaseDetails] = useState([]);
  const [error, setError] = useState(null); // Error state
  const [open, setOpen] = useState(false);
  const currDarkThemeClass = useThemeVal();
  const [tabValue, setTabValue] = useState('CropRun');
  const [searchParams, setSearchParams] = useSearchParams('');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerData, setDrawerData] = useState(null);
  
  const [selectedStates, setSelectedStates] = useState(["Active", "Planning", "Ready For Grow"]);

  const [viewBy, setViewBy] = useState('crop');

  const[alert , setAlert] = useState([]);
  const[routine,setRoutine] = useState([]);
  const[validate,setValidate] = useState([]);
  const [commandViewParams,setCommandViewParams] = useState(null);


  
  const tab = searchParams.get('tab');

  const { token, tokenDetails ,permissions} = useToken();

  const invalidTokenState = useMemo(() => failsTokenAndSetupCheck(token, tokenDetails), [token]);

  const handleSetTabValue = (event, newValue) => {
    setTabValue(newValue); // Update the selected tab
    setSearchParams({ tab: newValue }); // Update the URL to reflect the current tab

  };
  const handleOpen = () => {
    setOpen(true);
  };

  useEffect(()=>{
    if(invalidTokenState) return;
    const fetchData = async () =>{
      setLoading(true);
      try {

        const entityWithID = commandHomeEndpoint;
        const selectedStatuses = selectedStates;
        const queryParams =   selectedStates.map(s => `status=${encodeURIComponent(s)}`).join("&");
        // const url = `${commandHomeEndpoint}?${queryParams}`;
        let groupByParam = '';
        if (viewBy === 'crop') {
          groupByParam = '&group_by=crops';
        } else if (viewBy === 'strain') {
          groupByParam = '&group_by=strain';  // Use group_by for consistency
        }
        const url = `${commandHomeEndpoint}?${queryParams}${groupByParam}`;
        const response = await apiService.fetchInstance(url);

        if(response && response.data){
          const { table_data, cropplan_data, crops,strain, status_choices, phase_plan_details,alert,routine,validate} = response.data;
          setTableData(table_data);  
          // setPhaseDetails(phase_plan_details);
          
          // Use crops when viewBy is 'crop', otherwise use cropplan_data
          if(viewBy === 'crop' && crops){
            setData(crops);
          }else if(viewBy === 'strain' && strain) {
                    setData(strain);
                  }  else {
            setData(cropplan_data);
          }
          
          setStatusChoices(status_choices)
          setRoutine(routine);
          setValidate(validate);
          setAlert(alert);
        }
        else{
          console.log("No Data Received");
        } 

      }
      catch(error){
        console.error('error fetching data:',error.message)
        setError(error.message);
    }
    finally{
      setLoading(false);
    }
    };
    fetchData();
  },[invalidTokenState,selectedStates,viewBy]);

  useEffect(() => {
    if (tab) {
      setTabValue(tab); // Ensure `tabValue` matches `tabContentMap` keys
    } else {
      setTabValue('cropRun'); // Default to CropRun
      setSearchParams({ tab: 'cropRun' }); // Update query parameter
    }
  }, [tab]);

  const handleClose = () => {
    setOpen(false);
  };

  const permissionsList = useMemo(() => {
  if (!tokenDetails || !permissions) return {};
  return hasPermissions(
    ['commandcroprun', 'commandview'],
    { ...tokenDetails, permissions }
  );
}, [tokenDetails, permissions]);

  const canView = (model) => { return permissionsList?.[model]?.read ??   false;};
  const canEdit = (model) => {return permissionsList?.[model]?.read ?? false;};

  const tabs = {
    CROPRUN: 'cropRun',
    COMMANDVIEW: 'commandView',
  
  };
  
  const handleSubphaseClick = async (subphaseId) => {

    if (!subphaseId) {
        console.error("❌ Invalid Sub-Phase ID:", subphaseId);
        return;
    }

    try {
        setLoading(true);
        const baseURL = "command-center/home-details";
        const url = `${baseURL}/${subphaseId}/`;
        const response = await apiService.fetchInstance(url);
        if (!response || !response.data) {
            throw new Error("No response data received from API.");
        }
        setDrawerData(response.data);
        setIsDrawerOpen(true);
    } catch (error) {
        console.error("❌ API Fetch Error:", error);
        setError("Failed to fetch sub-phase details. Please try again.");
    } finally {
        setLoading(false);
    }
};
const handleSwitchToCommandView = (params) => {
  setCommandViewParams(params);
  setTabValue('commandView');
  setSearchParams({ tab: 'commandView' });
};


  const tabContentMap = {
    cropRun:canView('commandcroprun')?(
     <CropRunTab 
    data={data} 
    tableData={tableData}  
    statusChoices={statusChoices}
    // phaseDetails={phaseDetails}
    selectedStates={selectedStates}
    viewBy={viewBy}
    setViewBy={setViewBy}
    setSelectedStates={setSelectedStates} 
    onSubphaseClick={handleSubphaseClick} 
    drawerData={drawerData}
    canEdit={canEdit('commandcroprun')} 
    onSwitchToCommandView={handleSwitchToCommandView}
  
    routine={routine}
    validate={validate}
    alert={alert}
    />):(
      <NoPermissionMsg action="view" entity="Command Crop Run" />
    ),

    commandView: canView('commandview') ? (
    <CommandView 
      onClose={handleClose} 
      currDarkThemeClass={currDarkThemeClass} 
      canEdit={canEdit('commandview')}
     commandViewParams={commandViewParams} // Pass the parameters
  onSwitchToCommandView={handleSwitchToCommandView}
    />
  ) : (
    <NoPermissionMsg action="view" entity="Command View" />
  ),
  };
const LoadingSpinner = () => (
    <Box display="flex" justifyContent="center" alignItems="center" height="300px" width="100%">
        <CircularProgress thickness={5} size={30} />
        <Typography variant="h6" component="div" sx={{ ml: 2 }} className={`${cardClasses?.detailInfo?.titleTypo} ${currDarkThemeClass}`}>
        Loading Command Center Data
      </Typography>
    </Box>
  );

  
  return (
    <Box
    className={`customScroll ${currDarkThemeClass}`}
      sx={{
        fontFamily: 'Space Grotesk',
        fontStyle: 'normal',
        color: 'white', 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100vh', 
        width: '100%', 
        backgroundColor: currDarkThemeClass === 'dark-theme' ? 'black' : 'white',
        padding: '20px',
        overflowY: 'auto', 
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center', 
          marginBottom: '20px',
        }}
      >
        {/* Command Center Header */}
        <h1 style={{ color: currDarkThemeClass === 'dark-theme' ? '#D9DBD3' : 'black', marginRight: '20px' , fontSize: '24px',fontWeight: '500',lineHeight: '120%',fontFamily: 'Space Grotesk' }}>Command Center</h1>

        {/* Tab buttons next to the header */}
        <Box
          sx={{
            display: 'flex',
            
            borderRadius: '8px', 
            backgroundColor: 'gray', 
          }}
        >
          <Button
          onClick={(e) => handleSetTabValue(e, tabs.CROPRUN)}
          disabled={!canEdit('commandcroprun')}
            sx={{
              color: tabValue === tabs.CROPRUN ? '#1A1B19' : '#FFF',
              paddingTop: '6px',
              paddingLeft: '12px',
              paddingRight: '12px',
              fontSize: '14px',
              borderRadius: '10px 0 0 10px', 
              backgroundColor: tabValue === tabs.CROPRUN ? '#BDC0B2' : '#414437',
            }}
          >
            Crop Run
          </Button>
          <Button
           onClick={(e) => handleSetTabValue(e, tabs.COMMANDVIEW)}
          //  disabled = {!canEdit('commandview')}
            sx={{            
              color: tabValue === tabs.COMMANDVIEW ? '#1A1B19' : '#FFF',
              paddingTop: '6px',
              paddingBottom: '6px',
              paddingLeft: '12px',
              paddingRight: '12px',
              fontSize: '14px',
              borderRadius: '0 10px 10px 0',
              backgroundColor: tabValue === tabs.COMMANDVIEW ? '#BDC0B2' : '#414437', // Active or inactive color
            }}
          >
            Command View
          </Button>
          
        </Box>
        
      </Box>
      <Box>
      <Box
  sx={{
    
    width: '100%',
    height: '1px',  
    position: 'fixed',
    backgroundColor: '#414437',  
    position: 'relative', 
    marginTop: '3px', 
    marginBottom:'8px',
    zIndex: 1, 
  }}
/>
<Box sx={{ flexGrow: 1 }}>
  {loading ? (
    <LoadingSpinner/>
  ) : (
    tabContentMap[tabValue]
  )}
</Box>
</Box>
    </Box>
  );
}